import { Link, usePage } from "@inertiajs/react"
import { Fragment } from "react"

import Layout from "../../../shared/Layout"
import type { SharedData } from "../../../shared/shared"
import { useTripClock } from "../../../shared/tripClock"
// t030 impl-section (FR-3.4): the images domain's own shared section --
// the `links`/`stays` cross-domain TSX precedent this page already
// carries below.
import ImagesSection from "../../images/pages/ImagesSection"
import type { ImagesSectionProps } from "../../images/pages/ImagesSection"
import LinkPills from "../../links/pages/LinkPills"
import type { LinkProp } from "../../links/pages/LinkPills"
import StayRow, { isStayPast } from "../../stays/pages/StayRow"
import type { StayLabels, StayProps } from "../../stays/pages/StayRow"

interface ScheduleItemProps {
  id: number
  time: string
  // t015 `impl-day` (FR-2.4, FR-4.1): the fixed 24-hour zero-padded
  // machine key, deliberately bypassing I18n — compared against the trip
  // clock's own `now` (FR-1.5) to decide whether this item is past. The
  // displayed `time` above may change per locale; this key never does.
  time24: string
  title: string
  description: string | null
  // t018 `impl-pills` (FR-19, FR-22..FR-25): the item's own labelled
  // links, in `position` order — replaces the pre-t018 single `url`/
  // `linkLabel` pair.
  links: LinkProp[]
}

interface DayProps {
  heading: { title: string; subtitle: string }
  // t015 `impl-day` (FR-2.3): the page's own ISO date, compared against
  // the trip clock's own `today` (FR-1.5) to decide the one "past is
  // grey" rule (FR-4.1).
  date: string
  items: ScheduleItemProps[]
  // FR-10, FR-11, FR-15: the stays covering this day, in the order
  // `Stays::FindForDate` sent them -- `stayLabels` is chrome (the «Карта»
  // pill label and the drawer's separator), not per-stay data, so it
  // rides its own prop rather than living inside each stay's object.
  stays: StayProps[]
  stayLabels: StayLabels
  prevPath: string | null
  nextPath: string | null
  labels: {
    back: string
    previousDay: string
    nextDay: string
    emptyTitle: string
    emptyHint: string
    now: string
    // t030 impl-section (FR-3.5, FR-3.7): the images section's own
    // aria-label -- rides this existing chrome bundle rather than a
    // second new top-level prop, since FR-3.5 pins `images` as the only
    // new one.
    imagesSectionLabel: string
  }
  // t030 impl-section (FR-3.5): the day's own last section -- the whole
  // bundle `ImagesSection.tsx` needs, composed server-side by
  // `Images::ImagePresentation#images_section_props`. t031 impl-read
  // (FR-3.5): `ImagesSectionProps` now carries `days` too -- threaded
  // through unchanged, the `{...images}` spread below already carries it.
  images: ImagesSectionProps
}

// t015 `impl-day` (FR-4.1, FR-1.5, FR-1.6): the one rule, "past is grey" —
// a past day makes every item past; today makes an item past only when
// its time24 is strictly before now (an item at the current minute is
// still upcoming); a future day greys nothing. Plain string comparisons
// throughout, no parsing, no date library.
function isItemPast(date: string, time24: string, today: string, now: string): boolean {
  if (date < today) return true
  if (date === today) return time24 < now

  return false
}

const ARROW_CLASSES =
  "flex size-9 items-center justify-center rounded-full border border-gray-200"

interface ArrowLinkProps {
  path: string | null
  label: string
  pathData: string
}

// FR-10, FR-11, FR-12: an active arrow is a real Inertia link, carrying a
// translated accessible name, whose glyph is decorative (aria-hidden). An
// edge arrow (path === null) keeps the exact same position and geometry
// but is a plain span — not a link, not keyboard-focusable, exposed to
// assistive technology as unavailable via aria-disabled. Neither arrow is
// ever hidden or removed. The design contract draws both arrows as
// ordinary links and is silent on the dimmed state's own classes —
// opacity-40 is this page's addition (R-5).
function ArrowLink({ path, label, pathData }: ArrowLinkProps) {
  const icon = (
    <svg aria-hidden="true" width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path
        d={pathData}
        stroke="currentColor"
        className="text-gray-500"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )

  if (path === null) {
    return (
      <span
        role="link"
        aria-disabled="true"
        aria-label={label}
        className={`${ARROW_CLASSES} opacity-40`}
      >
        {icon}
      </span>
    )
  }

  return (
    <Link href={path} aria-label={label} className={ARROW_CLASSES}>
      {icon}
    </Link>
  )
}

// A row's content column carries pb-6 to separate it from the next row —
// except the last row, which also has no rail tail below its dot
// (FR-19): both are conditioned on position, not on the item's own data.
// t018 `impl-pills` (FR-25): min-w-0 lets a long link label wrap inside
// the pill row instead of forcing the column (and the page) to widen —
// a flex child otherwise refuses to shrink below its content's own
// intrinsic width.
const ROW_CONTENT_CLASSES = "flex min-w-0 flex-col gap-[3px] pb-6"
const LAST_ROW_CONTENT_CLASSES = "flex min-w-0 flex-col gap-[3px]"

interface TimelineRowProps {
  item: ScheduleItemProps
  isLast: boolean
  // t015 `impl-day` (FR-4.1, FR-4.5): drives the contract's grey set
  // (docs/design/day-page.html:71-75) — time, dot, rail tail, title and
  // description all step down together; nothing about the row's
  // structure changes.
  isPast: boolean
}

function TimelineRow({ item, isLast, isPast }: TimelineRowProps) {
  return (
    <li className="flex gap-3.5">
      <div
        className={`w-12 shrink-0 text-right text-[15px] font-semibold leading-[22px] tabular-nums ${
          isPast ? "text-gray-400" : "text-gray-900"
        }`}
      >
        {item.time}
      </div>
      <div className="flex shrink-0 flex-col items-center">
        <div
          className={`mt-[7px] size-[9px] rounded-full border-2 ${
            isPast ? "border-gray-300 bg-gray-300" : "border-accent"
          }`}
        />
        {!isLast && (
          <div className={`mt-1 w-0.5 grow ${isPast ? "bg-gray-100" : "bg-gray-200"}`} />
        )}
      </div>
      <div className={isLast ? LAST_ROW_CONTENT_CLASSES : ROW_CONTENT_CLASSES}>
        <div
          className={`text-[15px] font-semibold leading-[22px] ${
            isPast ? "text-gray-500" : "text-gray-900"
          }`}
        >
          {item.title}
        </div>
        {item.description !== null && (
          <div className={`text-[13px] leading-[18px] ${isPast ? "text-gray-400" : "text-gray-500"}`}>
            {item.description}
          </div>
        )}
        {/* t018 `impl-pills` (FR-22..FR-24): one wrapping pill row under
            the description (or under the title when there is none), in
            `position` order — a past item's pills grey, still working
            links; zero links render no row element at all. */}
        <LinkPills links={item.links} variant={isPast ? "grey" : "accent"} />
      </div>
    </li>
  )
}

interface NowLineProps {
  time: string
  label: string
}

// t015 `impl-day` (FR-4.2-FR-4.4): the «сейчас» line — not an item
// (`role="presentation"`, so it never joins a `listitem`-role query), the
// current trip-local time in the time column, an accent dot centred on
// the rail, an accent/60 rule across the content column, and the label.
// Rendered only on today's page when the day has items, between the last
// past item and the first upcoming one — above every item in the morning,
// below every item in the evening (FR-4.2, FR-4.3).
function NowLine({ time, label }: NowLineProps) {
  return (
    <li role="presentation" className="-mt-2.5 mb-3.5 flex items-center gap-3.5">
      <div className="w-12 shrink-0 text-right text-xs font-semibold leading-4 text-accent tabular-nums">
        {time}
      </div>
      <div className="flex w-[9px] shrink-0 justify-center">
        <span className="block size-[7px] rounded-full bg-accent" />
      </div>
      <div className="h-[1.5px] grow bg-accent/60" />
      <span className="text-xs leading-4 text-accent">{label}</span>
    </li>
  )
}

// The day page — the schedule domain's second surface (t008). Props
// mirror exactly what Schedule::DaysController#show sends. Implements
// docs/design/day-page.html's chrome and both states verbatim: the back
// link, the heading block, the arrow pair, and — depending on whether
// `items` is empty — either the ordered timeline or the empty state.
// Layout's own <main> classes are byte-identical to the contract's, so
// this page contributes children only (FR-22).
//
// t015 `impl-day` (FR-4.1): the past-item predicate is read here, once
// per item, from the trip clock (`useTripClock`, via the shared
// `tripTimezone` prop) — the same device-derived clock the main page
// reads, re-evaluated on the minute and on `visibilitychange`.
//
// t016 `impl-day-page` (FR-15, FR-18): the stays covering this day render
// above the timeline, in service order, through the `stays`-owned
// `StayRow`/`isStayPast` this page imports — the one place the
// `schedule` → `stays` arrow exists, in Ruby or in TypeScript. Nothing
// stay-related renders when `stays` is empty (FR-15).
//
// t018 `impl-pills` (FR-22, FR-37): `TimelineRow`'s own `LinkPills`
// import (above) is one of the two `schedule` → `links` arrows FR-37
// names (the other is `AdminDay.tsx`'s own import, `impl-pills` also) —
// each a plain component import, not a page.
//
// t030 impl-section (FR-3.1, FR-3.4): the «Картинки» section renders
// LAST, after the timeline or the empty state — on every day page, past
// days included, and never greyed (the past predicate below is never
// passed to it at all).
export default function Day({
  heading,
  date,
  items,
  stays,
  stayLabels,
  prevPath,
  nextPath,
  labels,
  images,
}: DayProps) {
  const {
    props: { tripTimezone },
  } = usePage<SharedData>()
  const { today, now } = useTripClock(tripTimezone)

  // t015 `impl-day` (FR-4.2, FR-4.3): the line renders only on today's own
  // page, and only when the day has items. `lineIndex` is the position to
  // insert it at within `items` (0..items.length, insert-before
  // semantics) — the first upcoming item's index, or `items.length` when
  // every item is past (the line falls after the last one).
  const showLine = date === today && items.length > 0
  const firstUpcomingIndex = items.findIndex(
    (item) => !isItemPast(date, item.time24, today, now),
  )
  const lineIndex = firstUpcomingIndex === -1 ? items.length : firstUpcomingIndex
  // A-9 (day-page.html:86): the rail must run down into the line's dot
  // rather than stop short, so the item the line falls right after keeps
  // its rail tail and pb-6 even when it is the last *item* by index — the
  // only case that changes is every item being past, where the line is
  // now the timeline's true last row instead of that item.
  const lineTakesLastRow = showLine && lineIndex === items.length

  return (
    <Layout>
      <Link href="/" className="flex items-center gap-1.5 text-sm text-accent">
        <svg aria-hidden="true" width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path
            d="M10 3.5L5.5 8l4.5 4.5"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        {labels.back}
      </Link>
      <div className="flex items-center justify-between gap-3">
        <div className="flex flex-col gap-0.5">
          <h2 className="text-[22px] font-bold leading-7 text-gray-900">{heading.title}</h2>
          <p className="text-[13px] leading-[18px] text-gray-500">{heading.subtitle}</p>
        </div>
        <div className="flex gap-2">
          <ArrowLink path={prevPath} label={labels.previousDay} pathData="M9.5 4L5.5 8l4 4" />
          <ArrowLink path={nextPath} label={labels.nextDay} pathData="M6.5 4l4 4-4 4" />
        </div>
      </div>
      {/* t016 `impl-day-page` (FR-15, FR-16): `key={date}` remounts the
          whole container (and every StayRow inside it, keyed by array
          index) on prev/next navigation, so a row is folded again on the
          neighbouring day even though Inertia swaps this component's
          props in place rather than remounting it (FR-16's "folded by
          default, always"). Zero stays renders no container at all. */}
      {stays.length > 0 && (
        <div key={date} className="flex flex-col gap-1.5">
          {stays.map((stay, index) => (
            <StayRow
              key={index}
              stay={stay}
              isPast={isStayPast(stay.checkOutDate, stay.checkOutTime24, today, now)}
              labels={stayLabels}
            />
          ))}
        </div>
      )}
      {items.length > 0 ? (
        <ol className="flex flex-col">
          {items.map((item, index) => (
            <Fragment key={item.id}>
              {showLine && lineIndex === index && <NowLine time={now} label={labels.now} />}
              <TimelineRow
                item={item}
                isLast={index === items.length - 1 && !lineTakesLastRow}
                isPast={isItemPast(date, item.time24, today, now)}
              />
            </Fragment>
          ))}
          {lineTakesLastRow && <NowLine time={now} label={labels.now} />}
        </ol>
      ) : (
        <div className="flex flex-col items-center gap-2 px-6 py-16 text-center">
          <p className="text-[15px] font-semibold leading-[22px] text-gray-900">
            {labels.emptyTitle}
          </p>
          <p className="text-[13px] leading-[18px] text-gray-500">{labels.emptyHint}</p>
        </div>
      )}
      {/* t030 impl-section (FR-3.1, FR-3.3): a `key` derived from `date`
          is the `stays` container's own ruling above, applied here too,
          so an in-flight batch or an open overlay never leaks across
          prev/next navigation -- suffixed so it never collides with the
          stays container's own sibling key of the same date.
          `emptyLine={null}`: an empty day renders the dashed upload
          button alone -- no grid, no empty-state illustration (unlike
          the tab, which shows one). The literal `uploadPath` the server
          sent is spread straight through with the rest of `images` --
          this page invents no address of its own.
          `uploadPlacement="bottom"` (owner ruling 2026-09-13, day-page.html
          state 1): here the dashed button sits BELOW the grid -- the
          pictures are the content -- while the tab keeps it above. */}
      <section
        key={`images-${date}`}
        className="flex flex-col gap-3"
        aria-label={labels.imagesSectionLabel}
      >
        <ImagesSection {...images} emptyLine={null} uploadPlacement="bottom" />
      </section>
    </Layout>
  )
}
